import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { ListPage } from '@/components/templates/list-page'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getJSON } from '@/lib/api'
import { OwnerFilter } from '@/components/OwnerFilter'

interface TaxReportRow {
  key: string
  tax_rate_id: number
  name: string
  kind: string
  percentage: string
  taxable_sales: string
  taxable_sales_cents: number
  tax_billed: string
  tax_billed_cents: number
  tax_collected: string
  tax_collected_cents: number
}

interface TaxReportResponse {
  tax_report: {
    starts_on: string
    ends_on: string
    rows: TaxReportRow[]
    net_sales: string
    tax_billed: string
    tax_collected: string
  }
}

export function TaxReportPage({ currentAssociationDate }: { currentAssociationDate?: string }) {
  const today = currentAssociationDate ?? new Date().toISOString().slice(0, 10)
  const initialRange = React.useMemo(
    () => ({ startsOn: monthStart(today), endsOn: today }),
    [today],
  )
  const [form, setForm] = React.useState(initialRange)
  const [range, setRange] = React.useState(initialRange)
  const [owner, setOwner] = React.useState<string | undefined>()
  const reportPath = taxReportPath(range, false, owner)
  const reportQuery = useQuery({
    queryKey: ['tax-report', range.startsOn, range.endsOn],
    queryFn: () => getJSON<TaxReportResponse>(reportPath),
    retry: false,
  })
  const rows = reportQuery.data?.tax_report.rows ?? []
  const totals = reportQuery.data?.tax_report

  return (
    <ListPage
      title="Tax report"
      toolbar={
        <div className="flex flex-wrap items-end gap-3">
          <RangeToolbar
            form={form}
            exportHref={taxReportPath(range, true, owner)}
            onChange={setForm}
            onApply={() => setRange(form)}
          />
          <OwnerFilter value={owner} onChange={setOwner} />
        </div>
      }
      table={{
        rows,
        getRowId: (row) => row.key,
        getRowLabel: (row) => row.name,
        state: reportQuery.isLoading ? 'loading' : reportQuery.isError ? 'error' : 'ready',
        loadingTitle: 'Loading Tax report',
        errorTitle: 'Tax report unavailable',
        emptyTitle: 'No Tax activity in this range',
        columns: [
          {
            id: 'kind',
            header: 'Kind',
            cell: (row) => row.kind,
          },
          {
            id: 'name',
            header: 'Tax Rate',
            cell: (row) => <span className="font-medium">{row.name}</span>,
          },
          {
            id: 'percentage',
            header: 'Percentage',
            headerClassName: 'text-right',
            className: 'text-right tabular-nums',
            cell: (row) => `${row.percentage}%`,
          },
          moneyColumn('net-sales', 'Net sales', (row) => row.taxable_sales),
          moneyColumn('tax-billed', 'Tax billed', (row) => row.tax_billed),
          moneyColumn('tax-collected', 'Tax collected', (row) => row.tax_collected),
        ],
        footer: totals ? (
          <div className="flex flex-wrap justify-end gap-x-6 gap-y-1 border-t border-border px-4 py-3 text-body-sm">
            <span>
              <strong>Net sales</strong> {totals.net_sales}
            </span>
            <span>
              <strong>Tax billed</strong> {totals.tax_billed}
            </span>
            <span>
              <strong>Tax collected</strong> {totals.tax_collected}
            </span>
          </div>
        ) : undefined,
      }}
    />
  )
}

function RangeToolbar({
  form,
  exportHref,
  onChange,
  onApply,
}: {
  form: { startsOn: string; endsOn: string }
  exportHref: string
  onChange: React.Dispatch<React.SetStateAction<{ startsOn: string; endsOn: string }>>
  onApply: () => void
}) {
  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        onApply()
      }}
    >
      <DateField
        label="Starts on"
        value={form.startsOn}
        onChange={(startsOn) => onChange({ ...form, startsOn })}
      />
      <DateField
        label="Ends on"
        value={form.endsOn}
        onChange={(endsOn) => onChange({ ...form, endsOn })}
      />
      <Button size="sm" type="submit">
        Apply range
      </Button>
      <Button size="sm" variant="outline" asChild>
        <a href={exportHref} download>
          <Download className="mr-2 h-4 w-4" strokeWidth={1.5} />
          Export CSV
        </a>
      </Button>
    </form>
  )
}

function DateField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const id = label.toLowerCase().replaceAll(' ', '-')
  return (
    <div className="space-y-1">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="h-9 w-40"
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function moneyColumn(id: string, header: string, cell: (row: TaxReportRow) => string) {
  return {
    id,
    header,
    headerClassName: 'text-right',
    className: 'text-right tabular-nums',
    cell,
  }
}

function monthStart(date: string) {
  return `${date.slice(0, 7)}-01`
}

function taxReportPath(
  range: { startsOn: string; endsOn: string },
  exportCsv = false,
  owner?: string,
) {
  const params = new URLSearchParams({ starts_on: range.startsOn, ends_on: range.endsOn })
  if (owner) params.set('owner', owner)
  const endpoint = exportCsv ? '/api/v1/tax_report/export' : '/api/v1/tax_report'
  return `${endpoint}?${params}`
}
